import ServiceTransaction from "./ServiceTransaction";
import { RequestOptions } from "./ServiceRequest";

class ServiceClient {
  // Array to track all ServiceTransactions
  private transactions: ServiceTransaction<any>[] = [];

  //  Having generic serviceresponse means you can get intellisense at design time and also it's much more declarative than just 
  //  receiving anything, but it does require there to be a match with what you write and what the server sends. 
  //  It also means that I have to create client side types for all data shapes coming back from the API, 
  //  although I am trading that for more confidence that the data is the same shapes as the code I have written
  private async request<T>(endPoint: string, method: string, options?: RequestOptions): Promise<ServiceTransaction<T>> {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    let url = apiBaseUrl + endPoint;

    options = options || {};

    // Create a new ServiceTransaction instance (which captures the request data)
    const transaction: ServiceTransaction<T> = this.newServiceTransaction<T>(url, method, options);

    try {

      // Ways in which this can go wrong, in attempted order of precedence
      // FAILURE
      // 1. Network error
      //      There is just no connectivity from the machine
      //      The API is down/unreachable. Are these the same thing? Also not the same as 503 server maintenance
      //      What does the browser do on a timeout vs a hard rejection (not there)
      // 2. Any non-OK (not 200+) status code BUT no ServiceResponse
      //      So the API has rejected the request but despite my best efforts, the client has no
      //      ServiceResponse to work with, i.e. api has done this, not me
      //      Upshot is there is no information available other than the status code
      // 3. Status code and ServiceResponse mismatch
      //      This means I have messed up and put the wrong code
      //      HTTP OK but ServiceResponse is notSuccess
      //      HTTP not OK but ServiceResponse isSuccess
      // 4. Any non-OK status code AND a ServiceResponse failure with more info
      //      This means I've detected something and sent back some info
      //      Could be anything, 400, 401, 403, 404, 500, 503
      //      Plan? Handle the code and check the response for any further info you can use
      // SUCCESS
      // 1. API returns a 200 status code and a success response
      // Kick it off
      // Maybe also I need to check what methods allow (or I allow) a body
      // rather than just add it if it's there

      let headers: Record<string, string> = {
        "Content-Type": "application/json"
      };

      const response = await fetch(url, {
        method,
        headers: headers,
        ...(options!.body! && { body: JSON.stringify(options!.body!) }),
        credentials: "include" // Ensures cookies are sent with the request
      });

      // response = http response
      // result = my service response
      
      // Assuming this is if HTTP code is not in the 200s
      // Would go hand in hand with API returning isSuccess: false
      if (!response.ok) {
        transaction.Failed(response.status, response.statusText);
        return transaction;
      }

      // JSON specific begin

      // What does this do if there's no body?
      const result = await response.json();

      // This is regardless of the HTTP code, what did the API return
      if (!result.isSuccess) {
        transaction.Failed(result.failure.code, result.failure.message);
        return transaction;
      }

      // JSON specific end

      transaction.Succeeded(result.success.data);
      return transaction;
    } catch (error: any) {
      transaction.Failed(0, "Unknown error");
      return transaction;
    }
  }

  public newServiceTransaction<T>(url: string, method: string, options?: RequestOptions): ServiceTransaction<T> {
    const transaction = new ServiceTransaction<T>(this.transactions.length + 1, url, method, options);
    this.transactions.push(transaction);
    // broadcast the new transaction to any listeners while it's in progress
    this.emitNewTransaction(transaction);
    return transaction;
  }

  // These are the JSON methods

  public async get<T>(endPoint: string, options?: RequestOptions): Promise<ServiceTransaction<T>> {
    return this.request<T>(endPoint, "GET", options);
  }

  public async post<T>(endPoint: string, options?: RequestOptions): Promise<ServiceTransaction<T>> {
    return this.request<T>(endPoint, "POST", options);
  }

  public async put<T>(endPoint: string, options?: RequestOptions): Promise<ServiceTransaction<T>> {
    return this.request<T>(endPoint, "PUT", options);
  }

  // Binary methods

  public async getBinary(endPoint: string, options?: RequestOptions): Promise<ServiceTransaction<Blob>> {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const url = apiBaseUrl + endPoint;

    // Create a new ServiceTransaction instance
    const transaction: ServiceTransaction<Blob> = this.newServiceTransaction<Blob>(url, "GET", options);

    // This needs weaving in with the normal requests, it's not that different
    
    // So it's returning JSON if it fails, but a Blob if it succeeds?
    // But it is just serving raw binary at this point, so the thing waiting can't handle an error can it?
    // That's quite a difference from the JSON methods

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/octet-stream" },
        credentials: "include" // Ensures cookies are sent with the request
      });

      if (!response.ok) {
        transaction.Failed(response.status, response.statusText);
        return transaction;
      }

      // Blob specific begin

      const blob = await response.blob();
      transaction.Succeeded(blob);

      return transaction;

      // Blob specific end
    } catch (error: any) {
      transaction.Failed(0, "Unknown error");
      return transaction;
    }
  }

  // Expose transactions for external access via custom event handler for new transactions
  private onNewTransactionCallbacks: Array<(transaction: ServiceTransaction<any>) => void> = [];

  public onNewTransaction(callback: (transaction: ServiceTransaction<any>) => void): () => void {
    this.onNewTransactionCallbacks.push(callback);
    return () => {
      this.offNewTransaction(callback);
    };
  }

  private emitNewTransaction(transaction: ServiceTransaction<any>): void {
    this.onNewTransactionCallbacks.forEach(cb => cb(transaction));
  }

  private offNewTransaction(callback: (transaction: ServiceTransaction<any>) => void): void {
    this.onNewTransactionCallbacks = this.onNewTransactionCallbacks.filter(cb => cb !== callback);
  }

}

export default ServiceClient;
